import React, { useEffect, useState } from "react";
import Modal from 'react-bootstrap/Modal';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import * as _ from "lodash";
import './EditContractModal.scss';
import EditContractDetails from './EditContractDetails';
import ContractDetailsConfirmation from './ContractDetailsConfirmation';
import PhysicalLocation from "domain/physicalLocation";
import * as ContractActions from "./contracts.actions";
import { DeliveryMethod, ExcludedDeliveryMethodsForDPContracts } from "domain/deliveryMethod";
import DeliveryPeriod from "domain/deliveryPeriod";
import ValidationError from "common/validationError";
import notificationService from "common/notificationService";
import Permissions from "common/security/permissions";
import { listPaymentOptionsForContract, PaymentOption } from "domain/paymentOption";
import FuturesContract from "domain/futuresContract";
import Contract, { getContractTypeName, ContractType } from "domain/contract";
import deliveryMethodService from "features/offers/deliveryMethodService";
import loggingService from "common/logging/loggingService";
import usePermission from "common/security/usePermission";
import { getTotalFees, isCashContractWithOpenStorage } from "./helpers";
import { FeeThin, getFeeThin } from "domain/fee";
import * as NP from "number-precision";
import * as MerchantActions from 'features/merchants/merchants.actions';
import { CustomField } from "domain/customField";
import AddCustomFieldModal from "features/offers/AddCustomFieldModal";
import contractTypeHelper from "domain/contractTypeHelper";
import { convertToCentralTime, isDateBetween } from "common/dateTimeHelpers";
import { ErpSystem } from "domain/erpSystem";
import { getCommodityBalanceType } from "domain/commodityBalance";
import useDeliveryPeriods from "features/deliveryPeriods/useDeliveryPeriods";
import useFuturesContracts from "features/futuresContracts/useFuturesContracts";
import useTraders from "features/settings/traders/useTraders";
import useLocations from "features/locations/useLocations";
import useCustomFields from "features/merchants/useCustomFields";
import { localizeCommon, localizeOfferContract } from "features/localization/localizeService";
import useReduxAction from "common/redux/useReduxAction";
import { useDispatch } from "react-redux";
import useStoreSelector from "common/redux/useStoreSelector";

export type EditContractProps = {
    contract: Contract;
    onClose(): void;
    onContractUpdated?: ((contract: Contract) => any);
};

const EditContractModal: React.FC<EditContractProps> = props => {

    const [isShowingConfirmation, showConfirmation] = useState(false);
    const [isSubmitting, showSubmitting] = useState(false);
    const [contract, setContract] = useState<Contract>(null);
    const [isFormValidated, setFormValidated] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [isLocationDetermineLater, setLocationDetermineLater] = useState(false);
    const [isPriceUpdated, setIsPriceUpdated] = useState(false);
    const [allowedDeliveryMethods, setAllowedDeliveryMethods] = useState<DeliveryMethod[]>([]);
    const [allowedPaymentOptions, setAllowedPaymentOptions] = useState<PaymentOption[]>([]);
    const [showContractCustomFields, setShowContractCustomFields] = useState(false);
    const [isCustomFieldsFormValidated, setCustomFieldsFormValidated] = useState(false);
    
    const futuresContracts = useFuturesContracts(props.contract.commodity.id);
    const deliveryPeriods = useDeliveryPeriods(props.contract.commodity.id);
    const customFields = useCustomFields();
    
    const canPlaceHedges = usePermission(Permissions.PlaceLimitOrder);
    const canPushBasis = usePermission(Permissions.PushBasis);

    const formRef = React.createRef<HTMLFormElement>();
    const customFieldsFormRef = React.createRef<HTMLFormElement>();

    const canEditContracts = usePermission(Permissions.EditContracts);
    const activeMerchant = useStoreSelector(m => m.userState.activeMerchant);
    const isCustomer = useStoreSelector(m => m.userState.isCustomer);
    const fees = useStoreSelector(m => m.merchantFees);
    const myId = useStoreSelector(m => m.userState.internalId);

    const locations = useLocations();
    const traders = useTraders();
    const myTrader = traders?.find(m => m.userId === myId);
    
    const dispatch = useDispatch();

    useReduxAction(() => {
        MerchantActions.loadMerchantFees()(dispatch);
    }, []);

    useEffect(() => {
        if (props.contract !== null && deliveryPeriods != null) {
            const contract = { ...props.contract };
            contract.deliveryPeriod = listDeliveryPeriods(contract)?.find(m => m?.id === contract.deliveryPeriod?.id);
            if (canPushBasis && contract.location == null && canPlaceHedges) {
                setLocationDetermineLater(true);
            }
            else if (contract.location == null)
                contract.location = listLocations()[0];

            if(activeMerchant.displayRegionOnAddOffer) {
                if(contract.region == null) {
                    contract.region =  contract.location == null ? 
                        listLocations().find(l => l.homeLocation == null) :
                        contract.location;
                }
                else if (contract.location != null) {
                    // Since location object coming from Offers API just have id and name
                    contract.location.homeLocation = listLocations().find(m => m.id === contract.location?.id)?.homeLocation;
                }
            }

            contract.comments = null;

            setContract(contract);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.contract, canPushBasis])

    useEffect(() => {
        if (contract?.contractType == null) return;

        setAllowedPaymentOptions(listPaymentOptionsForContract(contract.contractType));
        deliveryMethodService.list(contract.contractType)
            .then(setAllowedDeliveryMethods)
            .catch(err => {
                loggingService.error("Unable to retrieve delivery methods", err);
            });
    }, [contract?.contractType]);

    useEffect(() => {
        if (contract?.contractType == null || contract?.deliveryMethod == null) return;

        if(isCashContractWithOpenStorage(contract)) {
            setContract({...contract, expirationDate: contract.deliveryPeriodEndDate });
        }

    }, [contract?.contractType, contract?.deliveryMethod])

    function isLoaded(): boolean {
        return props.contract != null &&
            contract != null &&
            deliveryPeriods != null &&
            fees != null;
    }

    function goBack() {
        showConfirmation(false);
        showSubmitting(false);
        setFormValidated(false);
        setErrors([]);
    }

    function goNext() {
        if (formRef.current.checkValidity()) {
            if(!isCustomer && activeMerchant.useCustomFields && listCustomFields(contract).length > 0) {
                setShowContractCustomFields(true);
            }
            else {
                showConfirmation(true);
            }
        }

        setFormValidated(true);
    }

    function customFieldsGoBack() {
        setShowContractCustomFields(false);
    }

    function customFieldsGoNext() {
        if (customFieldsFormRef.current.checkValidity()) {
            showConfirmation(true);
        }

        setCustomFieldsFormValidated(true);
    }

    async function handleSubmit() {
        showSubmitting(true);
        try {
            await ContractActions.updateContract(contract)(dispatch);
            notificationService.success("Successfully updated contract!");
            props.onContractUpdated && props.onContractUpdated(contract);
            onClose();
        }
        catch (error) {
            showSubmitting(false);
            setErrors((error as ValidationError).errors ?? ["An unknown error occurred"]);
        }
    }

    function onClose() {
        showSubmitting(false);
        showConfirmation(false);
        setFormValidated(false);
        setShowContractCustomFields(false);
        setCustomFieldsFormValidated(false);
        setContract(null);
        setErrors([]);
        setLocationDetermineLater(false);
        setAllowedDeliveryMethods([]);
        setAllowedPaymentOptions([]);
        setIsPriceUpdated(false);
        props.onClose();
    }

    function listDeliveryPeriods(forContract: Contract): DeliveryPeriod[] {
        if (forContract == null) return [];

        let filteredDeliveryPeriods = deliveryPeriods;

        if (forContract.location != null && !contractTypeHelper.isHta(forContract.contractType)) {
            filteredDeliveryPeriods = deliveryPeriods.filter(dp => dp.bids.some(b => (b.location.id === forContract.location?.id || isLocationDetermineLater) && b.cash != null));
        }

        if (filteredDeliveryPeriods?.length > 0) {
            if (forContract.deliveryPeriod != null && !filteredDeliveryPeriods.some(x => x.id === forContract.deliveryPeriod.id)) {
                filteredDeliveryPeriods.push(forContract.deliveryPeriod);
            }
        }
        else {
            return [forContract.deliveryPeriod];
        }

        if(activeMerchant.erpSystem === ErpSystem.Agris && getCommodityBalanceType(forContract.deliveryMethod) != null) {
            const nowInCentralTime = convertToCentralTime(new Date());
            filteredDeliveryPeriods = filteredDeliveryPeriods.filter(dp => isDateBetween(nowInCentralTime, dp.start, dp.end) || dp.id === forContract.deliveryPeriod?.id);}
        
        return filteredDeliveryPeriods;
    }

    function listDeliveryMethods(forContract: Contract): DeliveryMethod[] {
        if (forContract == null) return [];

        if (forContract.contractType === ContractType.DelayedPrice && activeMerchant.hasErpIntegration){
            return allowedDeliveryMethods.filter(dm => !ExcludedDeliveryMethodsForDPContracts.includes(dm))
        }

        return allowedDeliveryMethods;
    }

    function listFuturesContracts(forContract: Contract): FuturesContract[] {

        if (forContract == null || forContract.commodity == null  || forContract.commodity?.isCashOnly || listDeliveryPeriods(forContract).length === 0) return [];

        if (isLocationDetermineLater || (canPushBasis && forContract.contractType === ContractType.Basis)) {
            return futuresContracts.data;
        }
        else if (canPushBasis) {
            const futureContracts = _.uniqBy(deliveryPeriods
                .filter(x => x.commodity.id === forContract.commodity.id
                    && (isLocationDetermineLater || x.bids.some(y => y.location.id === forContract.location?.id)))
                .map(x => x.contract), f => f.id);
            if (futureContracts?.length > 0) {
                if (forContract.futuresContract != null && !futureContracts.some(x => x.id === forContract.futuresContract.id)) {
                    futureContracts.push(forContract.futuresContract);
                }
                return futureContracts;
            }
            else {
                return [forContract.deliveryPeriod?.contract ?? forContract.futuresContract];
            }
        }
        else {
            return [forContract.deliveryPeriod?.contract ?? forContract.futuresContract];
        }
    }

    function listLocations(): PhysicalLocation[] {
        if(myTrader != null && myTrader.locations.length !== locations.length) {
            return _.orderBy(locations.filter(l => myTrader?.locations.some(tl => tl.id === l.id)), l => l.name);
        }
        
        return _.orderBy(locations, l => l.name);
    }
    
    function listFees(forContract: Contract): FeeThin[] {
        if(forContract == null || fees == null || fees.length === 0) return [];

        let allowedFees = fees
            .filter(f => (forContract.customerWillSell && f.allowSale || !forContract.customerWillSell && f.allowPurchase) &&
                f.allowedCommodities.includes(forContract.commodity?.id) &&
                f.allowedContractTypes.includes(forContract.contractType))
            .map(f => getFeeThin(f));

        // Adding the fees coming from parent contract while setting futures or basis
        if(forContract.sourceContractId != null){
            const missingParentFees = _.differenceBy(forContract.fees, allowedFees, m => m.id);
            allowedFees = [...allowedFees, ...missingParentFees];
        }

        return allowedFees;
    }

    function listCustomFields(forContract: Contract): CustomField[] {
        if(forContract == null || customFields == null || customFields.length === 0) return [];

        let allowedCustomFields = customFields
            .filter(f =>
                !f.mappedField &&
                (forContract.customerWillSell && f.allowSale || !forContract.customerWillSell && f.allowPurchase) &&
                (
                    (forContract.sourceContractId == null && f.allowedContractTypes.includes(forContract.contractType)) ||
                    (forContract.sourceContractId != null && f.allowedOfferTypes.includes(forContract.offer.type))
                ) &&
                f.allowedCommodities.includes(forContract.commodity.id)
            );
            
        return allowedCustomFields;
    }

    function onContractUpdated(forContract: Contract): void {

        //Make sure all the other values are still valid.
        forContract.location = listLocations().find(m => m.id === forContract.location?.id);
        forContract.deliveryMethod = listDeliveryMethods(forContract).find(m => m === forContract.deliveryMethod);
        const deliveryPeriods = listDeliveryPeriods(forContract);
        forContract.deliveryPeriod = deliveryPeriods.find(m => m?.id === forContract.deliveryPeriod?.id) ?? deliveryPeriods[0];

        let isLocationDetermineLater = forContract.location == null;
        setLocationDetermineLater(isLocationDetermineLater);

        if (!(isLocationDetermineLater || canPushBasis)) {
            forContract.futuresContract = forContract.deliveryPeriod?.contract;
        }

        if(forContract.cashPrice !== contract.cashPrice ||
            forContract.futuresPrice !== contract.futuresPrice){
            setIsPriceUpdated(true);
        }

        if (forContract.contractType === ContractType.DelayedPrice) {
            forContract.basis = null;
            forContract.futuresContract = null;
            forContract.deliveryPeriod = null;
            forContract.deliveryPeriodEndDate = null;
            forContract.deliveryPeriodStartDate = null;
            forContract.deliveryMethod = activeMerchant.hasErpIntegration ? forContract.deliveryMethod : DeliveryMethod.DelayedPrice;
        }
        forContract.finalPrice = getFinalPrice(forContract);

        setContract(forContract);
    }

    function getFinalPrice(forContract: Contract) {
        if(forContract == null) return 0;

        const price = contractTypeHelper.isHta(forContract.contractType) ? forContract.futuresPrice : forContract.cashPrice;
        const totalFees = getTotalFees(forContract);
        
        // price + premium - fees
        return NP.minus(NP.plus(price ?? 0, forContract.deliveryPeriod?.premium ?? 0), totalFees);
    }

    return !isLoaded()
        ? <></>
        : (
            <Modal show onHide={onClose} backdrop="static" >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {localizeOfferContract("EditContractModal_Header_EditContractNumber", {type: getContractTypeName(props.contract.contractType), contractId: props.contract.displayId})}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="add-contract-modal">
                    {props.contract.isSupervisorEditing && <Alert variant="warning">{localizeOfferContract("EditContractModal_Alert_SupervisorEdit")}</Alert>}
                    {!showContractCustomFields && !isShowingConfirmation &&
                        <Form ref={formRef} validated={isFormValidated}>
                            <EditContractDetails
                                contract={contract}
                                isLocationDetermineLater={isLocationDetermineLater}
                                canPlaceHedges={canPlaceHedges}
                                canPushBasis={canPushBasis}
                                locations={listLocations()}
                                traders={traders}
                                deliveryMethods={listDeliveryMethods(contract)}
                                deliveryPeriods={listDeliveryPeriods(contract)}
                                futuresContracts={listFuturesContracts(contract)}
                                paymentOptions={allowedPaymentOptions}
                                showPaymentOption={!activeMerchant.isPaymentOptionHidden}
                                onContractUpdated={onContractUpdated}
                                canEditContracts={canEditContracts}
                                isPriceUpdated={isPriceUpdated}
                                fees={listFees(contract)}/>
                        </Form>
                    }
                    {showContractCustomFields && !isShowingConfirmation &&
                        <Form ref={customFieldsFormRef} validated={isCustomFieldsFormValidated}>
                            <AddCustomFieldModal 
                                customFields={listCustomFields(contract)}
                                selectedCustomFields={contract?.customFields ?? []}
                                onCustomFieldsChanged={cf => setContract({...contract, customFields: cf})}
                            />
                        </Form>
                    }
                    {isShowingConfirmation && !isSubmitting &&
                        <>
                            <ContractDetailsConfirmation 
                                contract={contract} 
                                errors={errors} 
                                showPaymentOption={!activeMerchant.isPaymentOptionHidden} />
                        </>
                    }
                    {isShowingConfirmation && isSubmitting &&
                        <Spinner animation="border" />
                    }
                </Modal.Body>
                <Modal.Footer>
                    {!showContractCustomFields && !isShowingConfirmation &&
                        <>
                            <Button variant="secondary" onClick={onClose}>
                                {localizeCommon("Button_Cancel")}
                    </Button>
                            <Button variant="primary" onClick={goNext}>
                                {localizeCommon("Button_Next")}
                    </Button>
                        </>
                    }
                    {showContractCustomFields && !isShowingConfirmation &&
                        <>
                        <Button variant="secondary" onClick={customFieldsGoBack}>
                            {localizeCommon("Button_GoBackAndEdit")}
                        </Button>
                        <Button variant="primary" onClick={customFieldsGoNext} >
                            {localizeCommon("Button_Next")}
                        </Button>
                        </>
                    }
                    {isShowingConfirmation &&
                        <>
                            <Button variant="secondary" className="mr-auto" onClick={goBack} disabled={isSubmitting}>
                                {localizeCommon("Button_GoBackAndEdit")}
                    </Button>
                            <Button variant="secondary" onClick={() => { showConfirmation(false); onClose(); }} disabled={isSubmitting}>
                                {localizeCommon("Button_Cancel")}
                    </Button>
                            <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
                                {localizeOfferContract("EditContractModal_Button_SubmitContract")}
                    </Button>
                        </>
                    }
                </Modal.Footer>
            </Modal>
        );
}

export default EditContractModal;